import React, { useMemo, useState } from "react";
import type { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import formatErrorResponse from "helpers/form/formatErrorResponse";
import { useChangePassword } from "hooks/profile";
import type { ChangePasswordInterface } from "types/profile";
import { Button } from "components/ui/button";
import PasswordConstraints from "./PasswordConstraints";
import PasswordField from "./PasswordField";
import { createChangePasswordValidator, type PasswordFieldName } from "./changePasswordValidation";

const EMPTY_VALUES: ChangePasswordInterface = {
    current_password: "",
    new_password: "",
    confirm_password: "",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const ChangePasswordForm: React.FC = () => {
    const intl = useIntl();
    const changePasswordMutation = useChangePassword();
    const formatMessage = useMemo(
        () => (id: string) => intl.formatMessage({ id }),
        [intl]
    );
    const validate = useMemo(() => createChangePasswordValidator(formatMessage), [formatMessage]);
    const [visibleFields, setVisibleFields] = useState<Record<PasswordFieldName, boolean>>({
        current_password: false,
        new_password: false,
        confirm_password: false,
    });

    const toggleField = (name: PasswordFieldName) => {
        setVisibleFields((fields) => ({ ...fields, [name]: !fields[name] }));
    };

    const onSubmit = async (
        values: ChangePasswordInterface,
        form: FormApi<ChangePasswordInterface>,
    ) => {
        try {
            await changePasswordMutation.mutateAsync(values);
            toast.success(intl.formatMessage({ id: "profile.settings.security.password_changed" }));
            form.restart(EMPTY_VALUES);
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse(responseData as { data: unknown });
                }
            }
            throw err;
        }
    };

    return (
        <Form<ChangePasswordInterface>
            initialValues={EMPTY_VALUES}
            validate={validate}
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting, hasValidationErrors, values }) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
                    <PasswordField
                        name="current_password"
                        label={formatMessage("profile.settings.security.password.current")}
                        autoComplete="current-password"
                        visible={visibleFields.current_password}
                        showPasswordLabel={formatMessage("profile.settings.security.password.show")}
                        hidePasswordLabel={formatMessage("profile.settings.security.password.hide")}
                        onToggle={() => toggleField("current_password")}
                    />
                    <div className="flex flex-col gap-3">
                        <PasswordField
                            name="new_password"
                            label={formatMessage("profile.settings.security.password.new")}
                            autoComplete="new-password"
                            visible={visibleFields.new_password}
                            showPasswordLabel={formatMessage("profile.settings.security.password.show")}
                            hidePasswordLabel={formatMessage("profile.settings.security.password.hide")}
                            onToggle={() => toggleField("new_password")}
                        />
                        <PasswordConstraints password={values.new_password ?? ""} formatMessage={formatMessage} />
                    </div>
                    <PasswordField
                        name="confirm_password"
                        label={formatMessage("profile.settings.security.password.confirm")}
                        autoComplete="new-password"
                        visible={visibleFields.confirm_password}
                        showPasswordLabel={formatMessage("profile.settings.security.password.show")}
                        hidePasswordLabel={formatMessage("profile.settings.security.password.hide")}
                        onToggle={() => toggleField("confirm_password")}
                    />
                    <div className="flex justify-end">
                        <Button type="submit" disabled={submitting || hasValidationErrors} className="min-w-44 rounded-xl">
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                            {formatMessage("profile.settings.security.password.submit")}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};

export default ChangePasswordForm;
